<script lang="ts">
  import {DropdownButton} from '@/components/button';
  import {parseSummaries} from './Summaries';
  import {getDownloadOptionsContext} from '@/contexts/download';
  import {isMobile, user} from '@/store';
  import type {Video} from '@/types/project';
  import {download} from '@/utils/common';
  import {httpClient} from '@/utils/http-client';
  import {onMount} from 'svelte';
  export let selectedVideo: Video | null;

  const type = 'Summaries';
  let isLoadingSermonSummary = true;
  let sermonSummary: any;
  let textSermonSummary: string = '';
  let warningMessage: string = '';

  const handleDownload = async (extension: 'txt' | 'html' | 'pdf' = 'txt') => {
    const filename = `[${type}] ${selectedVideo?.title}`;
    // const text = textSermonSummary;
    const text = parseSummaries(sermonSummary, extension);

    await download(filename, text, type, extension);
  };

  const downloadOptions = getDownloadOptionsContext();

  async function regenerate() {
    if (!selectedVideo) throw 'selectedVideo is null';
    isLoadingSermonSummary = true;
    sermonSummary = null;
    await httpClient
      .get(`/sermon-summary/regenerate/${selectedVideo.id}`)
      .then(response => {
        const result = response.data;
        if (result) {
          sermonSummary = result.result?.summary || {};
          const text = parseSummaries(sermonSummary);
          textSermonSummary = text;
          isLoadingSermonSummary = false;
        } else {
          if (selectedVideo?.metadata && selectedVideo?.metadata?.duration < 300) {
            warningMessage =
              "The video you've uploaded is too short for our content generation system to work effectively. Please upload a video that's at least 5 minutes long to get AI-powered content.";
          } else {
            warningMessage = 'Failed to Generate Sermon Summary.';
          }
        }
      })
      .catch(error => {
        console.error(error);
        isLoadingSermonSummary = false;
      });
  }

  function parseSermonSummary(sermonSummary: any) {
    let text = '';

    for (const key in sermonSummary) {
      if (sermonSummary.hasOwnProperty(key)) {
        let title = key || '';
        let content = sermonSummary[key] || '';
        text += `${title}\n\n${content}\n\n\n`;
      }
    }

    return text;
  }

  onMount(async () => {
    if (!selectedVideo) throw 'selectedVideo is null';

    await httpClient
      .get(`/sermon-summary/${selectedVideo.id}`)
      .then(response => {
        const result = response.data;
        if (result) {
          sermonSummary = result.result?.summary || {};
          const text = parseSermonSummary(sermonSummary);
          textSermonSummary = text;
          isLoadingSermonSummary = false;
        } else {
          if (selectedVideo?.metadata && selectedVideo?.metadata?.duration < 300) {
            warningMessage =
              "The video you've uploaded is too short for our content generation system to work effectively. Please upload a video that's at least 5 minutes long to get AI-powered content.";
          } else {
            warningMessage = 'Failed to Generate Sermon Summary.';
          }
        }
      })
      .catch(error => {
        console.error(error);
        isLoadingSermonSummary = false;
      });
  });
</script>

<div class="h-full w-full">
  <div class="flex h-16 w-full items-center justify-between border-b border-b-gray-200 px-4 dark:border-b-zinc-600">
    {#if !$isMobile}
      <h2 class="text-md w-full font-bold text-gray-900">{type}</h2>
    {/if}
    <div class="flex w-full justify-center space-x-2 py-2 lg:justify-end lg:py-0">
      {#if $user.isImpersonated || $user.isAdmin}
        <button
          type="button"
          on:click={() => regenerate()}
          class="w-full rounded-md px-3 lg:w-auto py-1{isLoadingSermonSummary
            ? 'disabled cursor-progress bg-zinc-400 text-white'
            : 'hoversermonbg border border-primary bg-white text-primary hover:bg-primary/10'}"
          disabled={isLoadingSermonSummary}><i class="fa-solid fa-repeat"></i>&nbsp; Regenerate</button
        >
      {/if}
      <DropdownButton
        on:click={() => handleDownload()}
        on:optionClick={option => handleDownload(option.detail.value)}
        disabled={isLoadingSermonSummary}
        options={downloadOptions}
      >
        <span class="flex items-center gap-x-2">
          <i class="fa-solid fa-download" />Download
        </span>
      </DropdownButton>
    </div>
  </div>
  <div class="h-[calc(100%-4rem)] w-full overflow-auto p-4">
    {#if isLoadingSermonSummary}
      <div class="flex h-full w-full justify-center">
        <div class="flex flex-col items-center gap-4 pt-10">
          <div
            class="spinner h-8 w-8 !border-zinc-200 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          ></div>
          <h1 class="text-lg font-bold text-zinc-400 dark:text-zinc-400 lg:text-3xl">Loading Sermon Summary...</h1>
        </div>
      </div>
    {:else if sermonSummary != null}
      <div class="flex h-full w-full justify-center">
        <div class="h-full w-full max-w-[40rem]">
          <div class="grid gap-10 pb-16">
            {#each Object.entries(sermonSummary) as [key, value]}
              <div class="grid gap-2">
                <h1 class="text-lg font-bold lg:text-3xl">{key || ''}</h1>
                <p class="text-justify leading-loose">{@html value}</p>
              </div>
            {/each}
          </div>
        </div>
      </div>
    {:else}
      <div class="flex h-full w-full items-center justify-center">
        <div class="flex flex-col items-center justify-center gap-4">
          <h1 class="pt-10 text-3xl font-bold text-zinc-400 dark:text-zinc-400">No Sermon Summary Found</h1>
          <p class="text-zinc-400 dark:text-zinc-400">{warningMessage}</p>
        </div>
      </div>
    {/if}
  </div>
</div>
